import { useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import type AccessLevel from '@/SDK/AccessLevel'
import { PCInstance, CLInstance } from '@/SDK/index'
import { ClientEventType } from '@/SDK/ClientLogger'

// Used for client side authorization guard
export default function usePermissionChecker(permissionName: string, minAccessLevel: AccessLevel) {
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['permissionCheck', permissionName, minAccessLevel],
    queryFn: () => PCInstance.hasPermission(permissionName, minAccessLevel)
  })

  useEffect(() => {
    if (error) {
      navigate('/401?cause=no-permission')
    }
  }, [error, navigate])

  useEffect(() => {
    if (data === undefined || isLoading) return

    if (data.hasPermission) {
      CLInstance.log(ClientEventType.PERMISSION_CHECK, {
        permission: permissionName,
        requiredLevel: minAccessLevel,
        granted: true,
      })
    } else {
      CLInstance.log(ClientEventType.PERMISSION_DENIED, {
        permission: permissionName,
        requiredLevel: minAccessLevel,
        granted: false,
      })
      navigate('/401?cause=no-permission')
    }
  }, [data, isLoading, navigate, permissionName, minAccessLevel])

  return { isLoading }
}
